/**
 * Loyalty consent: whether this fan has agreed to take part in the loyalty programme.
 *
 * The flag lives in Auth0 `app_metadata.loyaltyoptin` and is read and written through this site's
 * own backend, because the tenant grants only a Management API client-grant -- no Auth0 Action, so
 * no custom claim, so no free read out of the token. See docs/subsystems/loyalty-consent.md.
 *
 * **Importing this module has no side effects.** It reads no cookie, starts no timer and makes no
 * request until something calls it, and it never owns an Auth0 client -- which is what makes it
 * structurally incapable of starting a login. The credential and the fan's `sub` both come from the
 * host through `configureLoyaltyConsent()`, which on this site wraps `window.urcAuth`. Nothing here
 * reads `document.cookie`, and there is no longer a cookie for it to read.
 *
 * **Not to be confused with `resources/scripts/tracking/consent.js`**, which is the OneTrust
 * analytics consent that gates the behavioural tracker. Two different consents, for two different
 * data flows, to the same vendor. This one is the loyalty programme; that one is analytics.
 *
 * @see ./machine.js  for the state machine and why the read fails closed
 * @see ./provider.js for the transport, and for the OneTrust seam
 * @see ./store.js    for why the cache is keyed by the fan rather than guarded
 */

import { authClaims, authReady, authState, idToken, onIdentityChange } from '../urcAuth.js';
import { createAuth0ConsentProvider } from './provider.js';
import { createClaimConsentProvider } from './claimProvider.js';
import { createConsentMachine, isDefiniteConsentDenial, STATE } from './machine.js';
import { createConsentStore } from './store.js';
import { createMarker, forcesDenial } from '../membership/marker.js';
import {
	LoyaltyConsentAuthError,
	LoyaltyConsentError,
	LoyaltyConsentRequiredError,
} from './errors.js';

export { LoyaltyConsentAuthError, LoyaltyConsentError, LoyaltyConsentRequiredError };

/**
 * The fan's membership marker (../membership/marker.js), read and written here for two reasons: a
 * status read carries the server's summary of the fan's latest join or leave, which is how a page
 * learns of a pending leg at no extra request; and in claim mode, where no request is made at
 * all, an open leave must still read as "not granted" whatever the ID-token claim says.
 */
const membershipMarker = createMarker( {
	getStorage: () => ( typeof window !== 'undefined' ? window.localStorage : null ),
} );

/** Cross-tab channel for our own writes. Distinct from `urc-auth`, which is the auth bundle's. */
const CONSENT_CHANNEL = 'urc-loyalty-consent';

/** Where the state is published, so a bug report can name it. */
const STATE_ATTRIBUTE = 'data-loyalty-consent';

/**
 * Asked for, and unable to answer.
 *
 * Not one of machine.js's STATE values, because the machine is not the thing that knows: this is a
 * property of the configuration, decided before any machine is built. It replaces the machine's
 * `off` wherever both would apply -- see `publishState()`.
 */
const STATE_MISCONFIGURED = 'misconfigured';

/** The machine, built on first use. */
let machine = null;

/** What the host handed us, if anything. */
let wiring = null;

/**
 * Point the module at the host's credential source.
 *
 * Idempotent and cheap. Call it once, as early as the host's entry can -- on this site that is the
 * first statement of `loyalty.jsx`'s module body, which runs after the document is parsed and
 * before `DOMContentLoaded`, and therefore before the cookie clear and before any block mounts.
 *
 * @param {Object}                options               Wiring.
 * @param {() => Promise<string>} options.getCredential The fan's **ID token**, or ''. Async, because
 *                                                      the Auth0 SDK answers asynchronously. Not
 *                                                      the access token -- see D-068.
 * @param {() => string}          options.getIdentity   The fan's `sub`, or ''. Synchronous, and
 *                                                      deliberately NOT derived from the bearer:
 *                                                      knowing WHO a read is for must not wait on
 *                                                      obtaining a token for them.
 * @param {Object}                [options.provider]    Override the store. For tests, and for the
 *                                                      day OneTrust becomes the source of truth.
 * @return {void}
 */
export function configureLoyaltyConsent( { getCredential, getIdentity, provider } ) {
	wiring = { getCredential, getIdentity, provider };
	machine = null;

	/*
	 * Say so, immediately, when the surface was asked for and cannot work.
	 *
	 * Eagerly rather than on first read, because the commonest visitor never triggers a read at all:
	 * `toasts/gate.js` settles `signed-out` at gate 1 for anybody not signed in, so nothing would
	 * ever publish a state for them and the misconfiguration would stay invisible on exactly the
	 * pages where its consequence -- the tracker refusing to collect -- is most visible.
	 *
	 * This is why the state exists: `enabled => true` with no Management credentials produced a
	 * silent no-op that was reported as a bug twice before it was recognised as one. See D-072.
	 */
	if ( config().misconfigured ) {
		publishState( STATE_MISCONFIGURED );

		if ( typeof console !== 'undefined' && console.warn ) {
			console.warn(
				'[urc-loyalty] loyalty consent is switched on but not configured — the Auth0 ' +
					'Management credentials are missing, so consent cannot be read. Nothing that ' +
					'depends on it will be permitted, including score-tracker analytics.'
			);
		}
	}
}

/**
 * Returns the loyalty consent state of the currently authenticated user.
 *
 * Resolves `false` if consent has never been set or no user is authenticated, and **never rejects**
 * for either of those reasons. Safe to call at any point after page load, including before the Auth0
 * session check has completed: the promise resolves once auth state is known.
 *
 * @return {Promise<boolean>} Whether consent is granted.
 */
export function isLoyaltyConsentGranted() {
	return live().isGranted();
}

/**
 * Persists the loyalty consent state for the currently authenticated user.
 *
 * @param {boolean} granted True to grant, false to revoke.
 * @return {Promise<void>} Resolves once Auth0 has confirmed the write.
 * @throws {LoyaltyConsentAuthError} When no user is authenticated. Nothing is persisted.
 */
export function setLoyaltyConsentGranted( granted ) {
	return live().setGranted( granted );
}

/**
 * Withdraw this fan's loyalty consent, and record the withdrawal even if we already believed it.
 *
 * **`setLoyaltyConsentGranted( false )` is NOT the same call, and the difference is not a nicety.**
 * That one costs no request when the cached answer already matches, which is exactly right for a
 * grant: the record already says the fan is in. A withdrawal is not that shape. The write carries a
 * second key -- `app_metadata.loyalty_optout_date` -- and the whole point of that key is to record
 * that this fan asked, on this day. A fan whose cached answer is already `false`, because another
 * tab or another device withdrew while this one had the dialog open, would otherwise press Leave
 * and produce nothing at all: no PATCH, no date, and no audit line. Silent on every surface.
 *
 * So this forces the write through. A repeat re-stamps the date with a later timestamp, which is a
 * true answer to the question that field asks -- when did this site last hear from them.
 *
 * Nothing else differs: same endpoint, same serialisation behind an in-flight write, same
 * write-through of the server's own answer, same cross-tab broadcast, same rejection behaviour.
 *
 * @return {Promise<void>} Resolves once Auth0 has confirmed.
 * @throws {LoyaltyConsentAuthError} When no fan is authenticated. Nothing is persisted.
 */
export function withdrawLoyaltyConsent() {
	return live().setGranted( false, { force: true } );
}

/**
 * Adopt a consent value the server confirmed on a membership call (../membership/), exactly as a
 * confirmed write would be: every tier, and every tab through the channel.
 *
 * @param {{granted: boolean, checkedAt: number}} answer The server's answer.
 * @return {void}
 */
export function applyLoyaltyConsentAnswer( answer ) {
	live().applyServerAnswer( answer );
}

/**
 * Subscribes to consent state changes.
 *
 * @param {(granted: boolean) => void} listener Called on each transition.
 * @return {() => void} Unsubscribe. Call it to prevent leaks.
 */
export function onLoyaltyConsentChange( listener ) {
	return live().onChange( listener );
}

/**
 * The full state, for diagnostics: `{granted, checkedAt, stale, reason, state}`.
 *
 * Exists because `Promise<boolean>` cannot say "we could not tell", and the difference between a
 * fan who declined and a Management API outage matters to whoever is reading a bug report.
 *
 * `state` is a `STATE` value from ./machine.js -- the machine's own last word, and the only thing
 * here that separates a withheld consent from an unreadable one. Anything acting on that difference
 * should use `isLoyaltyConsentDenied()` below rather than comparing the string itself.
 *
 * @return {{granted: boolean, checkedAt: number, stale: boolean, reason: string, state: string}} The state.
 */
export function readLoyaltyConsentState() {
	return live().readState();
}

/**
 * Whether this fan is known to have WITHHELD consent, as opposed to us being unable to read it.
 *
 * **`! isLoyaltyConsentGranted()` is not this, and the difference is the whole point.** That answers
 * `false` for a fan who declined, for a Management API outage, for a misconfigured install, for a
 * surface switched off by the config flag and for a browser with nobody signed in -- because it is a
 * `Promise<boolean>` and the gate it feeds must fail closed on every one of them. This narrows that
 * to the one case that is a *fan state* rather than a fault.
 *
 * **Who needs the distinction.** `../toasts/gate.js`, for the one subscriber on that chain that
 * navigates: `../nonMemberRedirect.js` sends a visitor who cannot use a members-only page somewhere
 * else, and a fan who declined cannot use it -- every self-scoped read is refused before the wire --
 * while a member during an outage can, once the outage ends. Moving the second one is the harm
 * D-062 exists to avoid, so a failed read has to be distinguishable from a refusal. Everything that
 * merely reveals or hides should keep asking `isLoyaltyConsentGranted()`.
 *
 * Synchronous, and correct only *after* an `isGranted()` has settled -- before that there is nothing
 * to report and it answers `false`, which is the safe direction. The decision itself lives in
 * ./machine.js beside the vocabulary it reads, where `machine.test.js` can drive it with no browser;
 * this is the wiring that points it at the live machine.
 *
 * @return {boolean} True only for a confirmed no.
 */
export function isLoyaltyConsentDenied() {
	return isDefiniteConsentDenial( live().readState().state );
}

/**
 * The machine, built against the real browser on first use.
 *
 * @return {Object} The machine.
 */
function live() {
	if ( machine ) {
		return machine;
	}

	const settings = config();
	// The ID token, not the access token: this endpoint is ours, and an access token is addressed
	// to the 3Halves loyalty API. See D-068 and ../../loyalty.jsx.
	const getCredential =
		wiring && typeof wiring.getCredential === 'function' ? wiring.getCredential : idToken;
	const getIdentity =
		wiring && typeof wiring.getIdentity === 'function' ? wiring.getIdentity : subjectOfState;

	return ( machine = createConsentMachine( {
		provider: withMembershipNotes( buildProvider( settings, getCredential ), getIdentity ),
		store: createConsentStore( {
			storage: safeSessionStorage(),
			now: () => Date.now(),
			ttlMs: ( Number( settings.clientTtl ) || 900 ) * 1000,
		} ),
		identity: getIdentity,
		whenAuthResolved: authResolver,
		subscribeLogout,
		consentChannel: openConsentChannel(),
		subscribeRevalidate,
		publish: publishState,
		now: () => Date.now(),
		logWarning: message => {
			if ( typeof console !== 'undefined' && console.warn ) {
				console.warn( `[urc-loyalty] ${ message }` );
			}
		},
	} ) );
}

/**
 * Note the server's membership summary on every read, and let an open leave override a claim.
 *
 * The override can only ever turn `true` into `false`, the journal's own rule on the server
 * (App\\Loyalty\\Membership\\Saga::forcesDenial()). In endpoint mode it is redundant -- the server
 * already answered `false` for an open leave, and the marker was just rewritten from that same
 * answer -- and in claim mode it is the only thing that makes a leave take effect before the fan's
 * next token.
 *
 * @param {Object|null}  provider    The provider, or null when the surface is off.
 * @param {() => string} getIdentity The fan's `sub`, or ''.
 * @return {Object|null} The same provider, noting as it reads.
 */
function withMembershipNotes( provider, getIdentity ) {
	if ( ! provider ) {
		return provider;
	}

	return {
		read: () =>
			provider.read().then( answer => {
				const sub = getIdentity();

				if ( ! sub || ! answer ) {
					return answer;
				}

				if ( 'membership' in answer ) {
					membershipMarker.write( sub, answer.membership );
				}

				if ( answer.granted === true && forcesDenial( membershipMarker.read( sub ) ) ) {
					return { ...answer, granted: false };
				}

				return answer;
			} ),
		write: granted => provider.write( granted ),
	};
}

/**
 * @param {Object}                settings      The `urcLoyalty.consent` block.
 * @param {() => Promise<string>} getCredential Credential source: the fan's ID token.
 * @return {Object|null} The provider, or null when the surface is switched off.
 */
function buildProvider( settings, getCredential ) {
	if ( wiring && wiring.provider ) {
		return wiring.provider;
	}

	// `enabled` arrives as a real boolean because it is nested: `wp_localize_script` stringifies
	// TOP-LEVEL scalars only, which is the same rule that makes `clientTtl` arrive as a number here
	// and `expiryMargin` used to arrive as '120'.
	if ( ! settings.enabled || ! settings.statusUrl || ! settings.setUrl ) {
		return null;
	}

	const endpoint = createAuth0ConsentProvider( {
		statusUrl: settings.statusUrl,
		setUrl: settings.setUrl,
		// The bound global, and not a wrapper. It used to matter for a reason that is gone:
		// functions.php monkey-patched `window.fetch` on every page to swallow any URL containing
		// `/authorize`, returning a promise that never settled. That patch is deleted; the bound
		// global stays because it is the plainer thing.
		transport: ( url, options ) => globalThis.fetch( url, options ),
		getCredential,
		// Already in the cached `urcLoyalty.consent` block (the leave dialog's deadline), so no
		// cached page is missing it; the provider's own default covers one that somehow is.
		writeTimeoutMs: Number( settings.writeDeadlineMs ) || undefined,
	} );

	if ( settings.readFrom !== 'claim' ) {
		return endpoint;
	}

	/*
	 * The claim path. Off unless an operator asks for it -- see config/three-halves.php's
	 * `read_from`, which carries the compliance argument.
	 *
	 * The endpoint provider is handed over as the WRITER rather than discarded: a claim can only be
	 * read, because the only way to change it is to change the `app_metadata` behind it, which is
	 * the Management API. So this is a read swap, never a replacement.
	 */
	return createClaimConsentProvider( {
		getClaims: authClaims,
		claimKey: settings.claimKey || 'loyaltyoptin',
		now: () => Date.now(),
		writer: endpoint,
	} );
}

/**
 * "Is there a fan, yes or no", once, from the one signal that answers it.
 *
 * **This replaces a three-legged composition whose worst case was about twenty-four seconds**, and
 * every leg of it was a workaround for something that no longer exists:
 *
 * 1. `whenSignInResolved` — a 500ms poll of the `auth0_authenticated` cookie for 24 ticks, because
 *    that cookie was written by an inline `wp_footer` probe's callback and routinely arrived after
 *    the deferred bundle had already read for it. It was the only signal in the bundle that could
 *    report a NO at all, and the NO was only trustworthy because it waited out the whole window.
 * 2. A credential already in hand — the ordinary page, answered immediately.
 * 3. A second 12s window on `whenCredentialArrives`, for the D-066 gap: signed in by cookie with
 *    the token cookie deleted by the third-party clear, or not yet minted. That watcher had no NO
 *    of its own, so the timer was what stopped a fan in the split state leaving this promise
 *    pending for the life of the page.
 *
 * `ready` answers all three. It settles once, on every branch — a fan, nobody, or a failure the SDK
 * could not classify — so it is terminal by construction rather than by deadline, and it does not
 * need a bearer in order to say whether there is a fan.
 *
 * @param {Function} listener Called once, when it is known whether there is a fan.
 * @return {() => void} Unsubscribe.
 */
function authResolver( listener ) {
	let wanted = true;

	authReady().then( () => {
		if ( wanted ) {
			listener();
		}
	} );

	return () => {
		wanted = false;
	};
}

/**
 * The fan's Auth0 `sub`, off the settled AuthState. The fallback when the host wires nothing.
 *
 * **Not the `auth0_user_id` cookie**, which `toasts/identity.js` has to defend against twice over:
 * it carries the literal `'unknown'` whenever `/userinfo` was unreadable, which two different fans
 * can both present, and duplicate domain-scoped copies of it exist in the wild. And not decoded out
 * of the bearer either, which is what this did until the SDK arrived: that bound the identity to a
 * token nobody had verified, and it made "who is this read for" depend on being able to obtain a
 * token for them. `auth/state.js` rejects an empty `sub` and the literal `'unknown'` outright, so
 * neither can reach a cache key.
 *
 * @return {string} The `sub`, or ''.
 */
function subjectOfState() {
	const state = authState();

	return state && typeof state.sub === 'string' ? state.sub : '';
}

/**
 * @return {Object} The `urcLoyalty.consent` block, or an empty object.
 */
function config() {
	if ( typeof window === 'undefined' || ! window.urcLoyalty ) {
		return {};
	}

	return window.urcLoyalty.consent || {};
}

/**
 * `sessionStorage`, or null where it is unavailable.
 *
 * Named to avoid shadowing the global of the same name for the whole module, which is the kind of
 * thing that reads fine until somebody adds a second caller.
 *
 * @return {Object|null} The storage, or null.
 */
function safeSessionStorage() {
	try {
		// Touched rather than merely referenced: browsers configured to block site data throw on
		// ACCESS, not on use, so the only way to know is to try.
		const storage = window.sessionStorage;

		storage.getItem( 'urc.loyalty.consent.probe' );

		return storage;
	} catch ( error ) {
		return null;
	}
}

/**
 * The cached answer stops belonging to whoever is in front of the browser.
 *
 * **`onIdentityChange` rather than a sign-out channel, and it is strictly wider.** This used to
 * listen on `BroadcastChannel('auth0-session')` for `auth0-login.php`'s `markLoggedOut()` message,
 * which reported a sign-out and nothing else — so one fan replacing another inside a tab, with no
 * sign-out in between, left the previous fan's consent answer in place. `onIdentityChange` fires
 * for a sign-out, for a switch, and for a cross-document switch this tab has not otherwise seen,
 * and the auth bundle's own `urc-auth` channel is what carries it between tabs.
 *
 * The machine's response is the same either way: `forget(STATE.ANON)`, and announce `false` to any
 * listener that was holding a `true`. A gate must not act on a value whose owner has left.
 *
 * @param {Function} listener Called when the fan changes.
 * @return {() => void} Unsubscribe.
 */
function subscribeLogout( listener ) {
	return onIdentityChange( () => listener() );
}

/**
 * Our own cross-tab channel, degrading to a no-op where BroadcastChannel is absent.
 *
 * @return {{post: Function, subscribe: Function}} The channel.
 */
function openConsentChannel() {
	if ( typeof BroadcastChannel === 'undefined' ) {
		return { post: () => {}, subscribe: () => () => {} };
	}

	let channel = null;

	try {
		channel = new BroadcastChannel( CONSENT_CHANNEL );
	} catch ( error ) {
		return { post: () => {}, subscribe: () => () => {} };
	}

	return {
		post: message => {
			try {
				channel.postMessage( message );
			} catch ( error ) {
				// A closed or unavailable channel costs the other tabs an immediate update and
				// nothing else; they re-read when their own TTL lapses.
			}
		},
		subscribe: listener => {
			const handler = event => listener( event && event.data );

			channel.addEventListener( 'message', handler );

			return () => {
				channel.removeEventListener( 'message', handler );
			};
		},
	};
}

/**
 * The three events that can reveal a change made outside this page.
 *
 * `pageshow` is in the list because some engines replay neither of the other two after a bfcache
 * restore. The same set, for the same reason, that `resources/scripts/auth/index.js` watches to
 * re-resolve the session, and that `../toasts/trigger.js` watches for its catch-up feed read.
 *
 * @param {Function} listener Called on each.
 * @return {() => void} Unsubscribe.
 */
function subscribeRevalidate( listener ) {
	if ( typeof window === 'undefined' ) {
		return () => {};
	}

	const events = [ 'visibilitychange', 'focus', 'pageshow' ];
	const handler = () => {
		if ( document.visibilityState === 'hidden' ) {
			return;
		}

		listener();
	};

	events.forEach( name => window.addEventListener( name, handler ) );

	return () => {
		events.forEach( name => window.removeEventListener( name, handler ) );
	};
}

/**
 * Publish the state onto `<html>`, so the reason is legible without a debugger.
 *
 * The `data-loyalty-toast` / `data-loyalty-redirect` precedent. It is the difference between "the
 * loyalty blocks are empty" and "the consent read is answering `unavailable`", which is the whole
 * of a support conversation.
 *
 * @param {string} state A STATE value from ./machine.js.
 * @return {void}
 */
function publishState( state ) {
	if ( typeof document === 'undefined' || ! document.documentElement ) {
		return;
	}

	// The machine publishes `off` whenever it has no provider, which is true of a surface that is
	// switched off AND of one that is broken. Only the composition root can tell those apart, so the
	// rewrite happens here rather than the machine being told about configuration it cannot use.
	const published = state === STATE.OFF && config().misconfigured ? STATE_MISCONFIGURED : state;

	document.documentElement.setAttribute( STATE_ATTRIBUTE, published );
}
